import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const _base = () => (window.themeData?.siteUrl ?? '').replace(/\/$/, '') + '/wp-content/themes/little-anvi/assets';

export const ALL_AGE_GROUPS = [
  { label: '0-3 Months',  slug: '0-3-months'  },
  { label: '3-6 Months',  slug: '3-6-months'  },
  { label: '6-12 Months', slug: '6-12-months' },
  { label: '12-24 Months',slug: '12-24-months'},
  { label: '2-3 Years',   slug: '2-3-years'   },
  { label: '3-4 Years',   slug: '3-4-years'   },
  { label: '4-5 Years',   slug: '4-5-years'   },
  { label: '5-6 Years',   slug: '5-6-years'   },
];

export default function ShopByAge({
  title    = 'Shop by Age',
  subtitle = 'Find the perfect products for every stage of growth! 👶',
}) {
  const siteUrl  = (window.themeData?.siteUrl ?? '').replace(/\/$/, '');
  const base     = _base();
  const trackRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(true);

  const visibleSlugs = window.themeData?.themeSettings?.visibleAgeGroups;
  const AGE_GROUPS = (
    Array.isArray(visibleSlugs) && visibleSlugs.length
      ? ALL_AGE_GROUPS.filter((g) => visibleSlugs.includes(g.slug))
      : ALL_AGE_GROUPS
  ).map((g) => ({ ...g, img: `${base}/${g.slug}.png` }));

  function buildUrl(slug) {
    return `${siteUrl}/shop/?age_group=${encodeURIComponent(slug)}`;
  }

  function updateArrows() {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }

  function scrollBy(dir) {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 300, behavior: 'smooth' });
  }

  return (
    <section className="py-14 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">

        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="inline-block text-xs font-black uppercase tracking-widest text-primary bg-primary/10 px-4 py-1.5 rounded-full mb-3">
              👶 Age Guide
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-textDark leading-tight">
              {title}
            </h2>
            <p className="text-textLight mt-2 text-sm">{subtitle}</p>
          </div>

          {/* Nav arrows */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-4">
            <button
              onClick={() => scrollBy(-1)}
              disabled={!canLeft}
              className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-gray-500 hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              disabled={!canRight}
              className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-gray-500 hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable track */}
        <div
          ref={trackRef}
          onScroll={updateArrows}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {AGE_GROUPS.map((group) => (
            <a
              key={group.slug}
              href={buildUrl(group.slug)}
              className="flex-shrink-0 snap-start group flex flex-col items-center cursor-pointer"
              style={{ width: 140 }}
            >
              {/* Image bubble — slightly smaller image so embedded text stays visible */}
              <div className="relative w-32 h-32 rounded-full flex items-center justify-center overflow-hidden transition-all duration-300 shadow-md group-hover:shadow-xl group-hover:scale-105" style={{ background: '#fffef5', border: '6px solid #f6effc' }}>
                <img
                  src={group.img}
                  alt={group.label}
                  className="w-[85%] h-[85%] object-contain"
                  loading="lazy"
                />
              </div>
            </a>
          ))}
        </div>

        {/* Mobile scroll hint dots */}
        <div className="flex justify-center gap-1.5 mt-5 sm:hidden">
          {AGE_GROUPS.map((g) => (
            <div key={g.slug} className="w-1.5 h-1.5 rounded-full bg-gray-300" />
          ))}
        </div>
      </div>
    </section>
  );
}
